import { TableIcon } from "lucide-react";
import {
  ComponentProps,
  Fragment,
  Suspense,
  useMemo,
  useRef,
  useState,
} from "react";
import { Column } from "react-table";

import {
  GetRawViewsDocument,
  RawViewFragment,
  useGetDwDirectoryItemsSuspenseQuery,
  useGetDwDirectorySuspenseQuery,
  useImportDwDirectoryMutation,
  useRawViewLazyQuery,
  useViewDetailsByPathSuspenseQuery,
} from "@/apollo/types";
import { Button, ButtonPrimitive } from "@/components/elements/Button";
import { CloseButton } from "@/components/elements/CloseButton";
import DataSchemaTable from "@/components/elements/DataSchemaTable";
import { List } from "@/components/elements/List";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
} from "@/components/elements/Modal";
import { Heading } from "@/components/elements/Typography";
import { Input } from "@/components/primitives/input";
import cn from "@/helpers/classNames";
import { IntegrationLogo } from "@/integrations";
import { useDataWarehouseContext } from "@/providers/DataWarehouseProvider";
import { useToast } from "@/providers/ToastProvider";
import { useCurrentAccount } from "@/providers/account";
import { Tab, Transition } from "@headlessui/react";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { FolderIcon } from "@heroicons/react/24/outline";
import { ErrorBoundary } from "@sentry/react";
import { useVirtualizer } from "@tanstack/react-virtual";

export function ImportViewDialog({
  onViewSelected,
  ...modalProps
}: ComponentProps<typeof Modal> & {
  onViewSelected: (view: RawViewFragment) => void;
}) {
  return (
    <Modal {...modalProps}>
      <ImportViewContainer onViewSelected={onViewSelected} />
    </Modal>
  );
}

function ImportViewContainer(props: {
  onViewSelected: (view: RawViewFragment) => void;
}) {
  const toast = useToast();
  const dwh = useDataWarehouseContext();

  const [fetchRawView] = useRawViewLazyQuery();
  const [importViews, { loading: isImportingTable }] =
    useImportDwDirectoryMutation({
      refetchQueries: [
        {
          query: GetRawViewsDocument,
        },
      ],
    });

  const handleImport = async (params: {
    database?: string;
    schema: string;
    tables: string[];
  }) => {
    const importResult = await importViews({
      variables: {
        input: {
          connectionId: dwh.connectionId,
          ...params,
        },
      },
    });

    const table = importResult.data?.importDWDirectory[0];
    if (!table?.viewId) {
      toast(
        "Failed to import table",
        table?.error || "An error occurred while importing the table.",
        "error",
        10_000,
      );
      return;
    }
    const rawViewResult = await fetchRawView({
      variables: {
        viewId: table.viewId,
      },
    });
    const rawView = rawViewResult.data?.rawView;
    if (!rawView) {
      toast(
        "Raw View not found",
        rawViewResult.error?.message ||
          "An error occurred while fetching the raw view.",
        "error",
      );
      return;
    }
    const tableName = `${rawView.schema}.${rawView.sourceStream}`;
    toast(`Table ${tableName} imported successfully`, "", "success");
    return props.onViewSelected(rawView);
  };

  return (
    <>
      <ModalHeader>
        <div className="inline-flex items-center">
          Import Table from {dwh.integration.name}
          <IntegrationLogo id={dwh.integration.id} className="mx-1 h-5 w-5" />
        </div>
        <div className="mt-2 text-sm text-muted-foreground">
          Select a table from your Data Warehouse to import into Weld and use in
          your syncs.
        </div>
      </ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <DataWarehouseBrowser
          onImportTable={handleImport}
          isImportingTable={isImportingTable}
        />
      </ModalBody>
    </>
  );
}

function DataWarehouseBrowser(props: {
  isImportingTable?: boolean;
  onImportTable?: (params: {
    database?: string;
    schema: string;
    tables: string[];
  }) => Promise<void>;
}) {
  const [path, setPath] = useState<
    {
      segment: string;
      hasMoreLevels: boolean;
      isRoot?: boolean;
      searchQuery?: string;
    }[]
  >([
    {
      segment: "Root",
      hasMoreLevels: true,
      isRoot: true,
    },
  ]);

  const [selectedTablePath, setSelectedTablePath] = useState<
    string[] | undefined
  >(undefined);

  const account = useCurrentAccount();
  const dwh = useDataWarehouseContext();

  const handleImportTable = async (fullPath: string[]) => {
    const firstNonRootIndex = path.findIndex((x) => !x.isRoot);
    if (firstNonRootIndex === -1) {
      // eslint-disable-next-line no-console
      console.warn("No valid path found for table selection.");
      return;
    }
    const firstNonRootPath = path[firstNonRootIndex];
    const database = firstNonRootPath.hasMoreLevels
      ? firstNonRootPath.segment
      : undefined;
    const schema = firstNonRootPath.hasMoreLevels
      ? path[firstNonRootIndex + 1].segment
      : firstNonRootPath.segment;
    const tables = fullPath.slice(-1);
    await props.onImportTable?.({ database, schema, tables });
  };

  const handleSelectTable = (fullPath: string[]) => {
    setSelectedTablePath(fullPath);
  };

  return (
    <div>
      <Tab.Group
        selectedIndex={path.length}
        onChange={(index) => {
          setSelectedTablePath(undefined);
          setPath((prev) => {
            return prev.slice(0, index + 1);
          });
        }}
      >
        <Tab.List className={cn("flex pb-1")}>
          {path.map(({ segment, isRoot }, index, arr) => (
            <Fragment key={index}>
              <Tab key={index} className={cn("flex items-center")}>
                {isRoot ? (
                  <Button
                    as="div"
                    role="button"
                    variant="ghost"
                    icon={<IntegrationLogo id={dwh.integration.id} />}
                    className={cn("text-sm", {
                      "text-muted-foreground": index !== path.length - 1,
                    })}
                    size="xs"
                  >
                    {account.dataWarehouse?.title}
                  </Button>
                ) : (
                  <Button
                    as="div"
                    role="button"
                    variant="ghost"
                    className={cn("truncate text-sm", {
                      grow: index === path.length - 1,
                      shrink: index < path.length - 1,
                    })}
                    size="xs"
                  >
                    {segment}
                  </Button>
                )}
              </Tab>
              {index < arr.length - 1 && (
                <span className="mx-1 leading-5 text-muted-foreground">/</span>
              )}
            </Fragment>
          ))}
        </Tab.List>
        <div className="flex h-96 divide-x border">
          <div className="flex-1 overflow-hidden">
            <Tab.Panels className="h-full grow">
              <div className="relative h-full">
                {path.map((segment, i) => {
                  const subPath = path.slice(0, i + 1);
                  return (
                    <Transition
                      key={i}
                      show={i === path.length - 1}
                      className="h-full"
                    >
                      <Tab.Panel className={"h-full"}>
                        <ErrorBoundary>
                          <Suspense
                            fallback={
                              <div className="flex items-center gap-2 p-4">
                                <LoadingSpinner className="h-5 w-5" />
                                <span className="text-sm text-muted-foreground">
                                  Loading directory contents. This may take a
                                  moment...
                                </span>
                              </div>
                            }
                          >
                            {segment.hasMoreLevels ? (
                              <DirectoriesView
                                path={subPath}
                                searchQuery={segment.searchQuery}
                                onSearchQueryChange={(query) => {
                                  setPath((prev) => {
                                    const newPath = [...prev];
                                    newPath[i] = {
                                      ...newPath[i],
                                      searchQuery: query,
                                    };
                                    return newPath;
                                  });
                                }}
                                onSelectDirectory={(segment, hasMoreLevels) => {
                                  setPath((prev) => [
                                    ...prev,
                                    { segment, hasMoreLevels },
                                  ]);
                                }}
                              />
                            ) : (
                              <TablesView
                                path={subPath}
                                selectedTablePath={selectedTablePath}
                                onSelectTable={handleSelectTable}
                                onImportTable={handleImportTable}
                              />
                            )}
                          </Suspense>
                        </ErrorBoundary>
                      </Tab.Panel>
                    </Transition>
                  );
                })}
              </div>
            </Tab.Panels>
          </div>
          {selectedTablePath && (
            <div className="h-full w-3/5">
              <ErrorBoundary>
                <Suspense
                  fallback={
                    <div className="p-4">
                      <LoadingSpinner className="h-5 w-5" />
                    </div>
                  }
                >
                  <InspectTablePanel
                    path={selectedTablePath}
                    isImportingTable={props.isImportingTable}
                    onImportTable={() => handleImportTable(selectedTablePath)}
                    onClose={() => setSelectedTablePath(undefined)}
                  />
                </Suspense>
              </ErrorBoundary>
            </div>
          )}
        </div>
      </Tab.Group>
    </div>
  );
}

const columns: Column[] = [
  {
    Header: "Column Name",
    accessor: "name",
  },
  {
    Header: "Type",
    accessor: "type",
  },
];

function InspectTablePanel(props: {
  path: string[];
  isImportingTable?: boolean;
  onImportTable: () => Promise<void>;
  onClose: () => void;
}) {
  const { data } = useViewDetailsByPathSuspenseQuery({
    variables: {
      path: props.path,
    },
  });

  const tableName = props.path.at(-1) || "Unnamed Table";
  return (
    <div className="relative flex h-full flex-col pt-2">
      <Heading className="mb-1 w-full truncate px-4 pr-10 text-sm">
        {tableName}
      </Heading>
      <CloseButton
        onClick={() => props.onClose()}
        className="absolute right-1 top-1"
        size="sm"
      />
      <div className="grow overflow-auto px-2 py-2">
        <DataSchemaTable
          columns={columns}
          data={data.viewDetailsByPath.dataSchema}
          className="border-none"
        />
      </div>
      <div className="mt-auto flex justify-end p-2">
        <Button
          variant="solid"
          colorScheme="primary"
          onClick={() => props.onImportTable()}
          className="w-full"
          isLoading={props.isImportingTable}
          loadingText="Importing..."
        >
          <span className="min-w-0 truncate">
            Import <em>{tableName}</em>
          </span>
        </Button>
      </div>
    </div>
  );
}

function DirectoriesView(props: {
  searchQuery?: string;
  onSearchQueryChange: (query: string) => void;
  path: { segment: string; isRoot?: boolean; hasMoreLevels: boolean }[];
  onSelectDirectory: (segment: string, hasMoreLevels: boolean) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const searchQuery = props.searchQuery || "";

  const pathRef = useRef(
    props.path.flatMap((x) => (x.isRoot ? [] : x.segment)),
  );

  const dwh = useDataWarehouseContext();
  const { data } = useGetDwDirectorySuspenseQuery({
    variables: {
      input: {
        connectionId: dwh.connectionId,
        path: pathRef.current,
      },
    },
  });

  const directories = data.getDWDirectory.children;

  const filteredDirectories = useMemo(() => {
    if (searchQuery.trim() === "") {
      return directories;
    }
    return directories.filter((dir) =>
      dir.toLowerCase().includes((searchQuery || "").toLowerCase()),
    );
  }, [searchQuery, directories]);

  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: filteredDirectories.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 10,
  });

  return (
    <div className="flex h-full flex-col">
      <div className="relative mb-2 border-b">
        <div className="absolute left-0 top-0 flex h-full w-10 items-center justify-center">
          <MagnifyingGlassIcon className="h-4 text-muted-foreground" />
        </div>
        <Input
          ref={inputRef}
          autoFocus
          value={searchQuery}
          onChange={(e) => {
            props.onSearchQueryChange(e.target.value);
          }}
          placeholder="Search directories..."
          size="sm"
          className="border-none pl-10 pr-2 text-sm focus:ring-0"
        />
        {searchQuery.trim().length > 0 && (
          <div className="absolute right-0 top-0 z-10 flex h-full items-center px-2">
            <ButtonPrimitive
              className="opacity-50 hover:opacity-100"
              onClick={() => {
                props.onSearchQueryChange("");
                inputRef.current?.focus();
              }}
            >
              <XMarkIcon className="h-5 w-5" />
            </ButtonPrimitive>
          </div>
        )}
      </div>
      {data.getDWDirectory.children.length === 0 && (
        <div className="text-center text-sm text-muted-foreground">
          No directories found.
        </div>
      )}
      <div ref={parentRef} className="grow overflow-y-auto">
        <List
          className="px-0 py-0 text-sm"
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: "100%",
            position: "relative",
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const directory = filteredDirectories[virtualRow.index];
            return (
              <List.ItemButton
                key={virtualRow.index}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                as="li"
                role="button"
                onClick={() => {
                  props.onSelectDirectory(
                    directory,
                    data.getDWDirectory.hasNext,
                  );
                }}
                className="w-full"
              >
                <List.ItemIcon>
                  <FolderIcon className="h-4 w-4" />
                </List.ItemIcon>

                {directory}
              </List.ItemButton>
            );
          })}
        </List>
      </div>
    </div>
  );
}

function TablesView(props: {
  path: { segment: string; isRoot?: boolean; hasMoreLevels: boolean }[];
  selectedTablePath?: string[];
  onSelectTable: (path: string[]) => void;
  onImportTable: (path: string[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const pathRef = useRef(
    props.path.flatMap((x) => (x.isRoot ? [] : x.segment)),
  );

  const dwh = useDataWarehouseContext();
  const { data } = useGetDwDirectoryItemsSuspenseQuery({
    variables: {
      input: {
        connectionId: dwh.connectionId,
        path: pathRef.current,
      },
    },
  });

  const tables = data.getDWDirectoryItems;

  const filteredTables = useMemo(() => {
    if (searchQuery.trim() === "") {
      return tables;
    }
    return tables.filter((table) => {
      const tableName = table.path.at(-1) || "";
      return tableName.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [searchQuery, tables]);

  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: filteredTables.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 10,
  });

  return (
    <div className="flex h-full flex-col">
      <div className="relative mb-2 border-b">
        <div className="absolute left-0 top-0 flex h-full w-10 items-center justify-center">
          <MagnifyingGlassIcon className="h-4 text-muted-foreground" />
        </div>
        <Input
          ref={inputRef}
          autoFocus
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          placeholder="Search tables..."
          size="sm"
          className="border-none pl-10 pr-2 text-sm focus:ring-0"
        />
        {searchQuery.trim().length > 0 && (
          <div className="absolute right-0 top-0 z-10 flex h-full items-center px-2">
            <ButtonPrimitive
              className="opacity-50 hover:opacity-100"
              onClick={() => {
                setSearchQuery("");
                inputRef.current?.focus();
              }}
            >
              <XMarkIcon className="h-5 w-5" />
            </ButtonPrimitive>
          </div>
        )}
      </div>
      {data.getDWDirectoryItems.length === 0 && (
        <div className="text-center text-sm text-muted-foreground">
          No tables found.
        </div>
      )}
      <div ref={parentRef} className="grow overflow-y-auto">
        <List
          className="px-0 py-0 text-sm"
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: "100%",
            position: "relative",
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const table = filteredTables[virtualRow.index];
            return (
              <List.ItemButton
                key={virtualRow.index}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                as="li"
                role="button"
                onClick={() => {
                  props.onSelectTable(table.path);
                }}
                className={cn("w-full", {
                  "bg-foreground/5":
                    props.selectedTablePath?.join(".") === table.path.join("."),
                })}
              >
                <List.ItemIcon>
                  <TableIcon className="h-4 w-4" />
                </List.ItemIcon>
                {table.path.at(-1) || "Unnamed Table"}
              </List.ItemButton>
            );
          })}
        </List>
      </div>
    </div>
  );
}
